import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { api } from '@/services/api'
import { i18n } from '@/i18n'

const SESSION_KEY = 'pulsewatch.admin-token'

export const useAuthStore = defineStore('auth', () => {
  const token = ref(sessionStorage.getItem(SESSION_KEY) || '')
  const oidcAuthenticated = ref(false)
  const authenticated = computed(() => token.value.length > 0 || oidcAuthenticated.value)
  let initialized: Promise<void> | undefined

  function initialize(): Promise<void> {
    initialized ??= api
      .session()
      .then((session) => {
        oidcAuthenticated.value = session.authenticated
        api.setCsrfToken(session.csrf_token || '')
        if (session.authenticated) {
          token.value = ''
          sessionStorage.removeItem(SESSION_KEY)
        }
      })
      .catch(() => {
        initialized = undefined
      })
    return initialized
  }

  async function login(candidate: string): Promise<void> {
    const clean = candidate.trim()
    if (!clean) throw new Error(i18n.global.t('errors.adminTokenRequired'))
    await api.validateToken(clean)
    token.value = clean
    sessionStorage.setItem(SESSION_KEY, clean)
  }

  function clearLocalSession(): void {
    oidcAuthenticated.value = false
    initialized = Promise.resolve()
    api.clearQueryCache()
    token.value = ''
    sessionStorage.removeItem(SESSION_KEY)
    api.setCsrfToken('')
  }

  async function logout(options: { provider?: boolean } = {}): Promise<string> {
    const result = await api.logout(Boolean(options.provider && oidcAuthenticated.value))
    clearLocalSession()
    return result.redirect_url || '/auth/logout/complete'
  }

  return { token, authenticated, oidcAuthenticated, initialize, login, logout, clearLocalSession }
})
